import { HTMLAttributes } from 'react'
import { Box, Container, Grid, Typography } from '@mui/material'
import parse from 'html-react-parser'

import Button from '@/components/base/button'

import { WPLink } from '@/types'

export type ParagraphProps = HTMLAttributes<HTMLDivElement> & {
  content: {
    pretitle: string
    title: string
    heading: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
    subtitle: string
    text: string
  }
  button: {
    link: WPLink
    icon: string
  }
  alignment: 'left' | 'center' | 'right' | 'full'
  is_first?: boolean
}

const Paragraph = ({ content, button, alignment, is_first, ...props }: ParagraphProps) => {
  return (
    <Box {...props}>
      <Container>
        <Grid container>
          <Grid
            size={{ xs: 4, sm: 6, md: alignment === 'full' ? 10 : 6 }}
            offset={{
              xs: 0,
              sm: alignment === 'center' ? 1 : alignment === 'left' || alignment === 'full' ? 0 : 2,
              md: alignment === 'center' ? 3 : alignment === 'left' || alignment === 'full' ? 1 : 5
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                textAlign: 'left',
                gap: 2
              }}
            >
              {content.pretitle ? (
                <Typography
                  variant="overline"
                  color="text.secondaryContrastText"
                  sx={{ width: '100%' }}
                >
                  {content.pretitle}
                </Typography>
              ) : null}
              {content.title ? (
                <Typography variant={content.heading} component={is_first ? 'h1' : 'h2'}>
                  {content.title}
                </Typography>
              ) : null}
              {content.subtitle ? (
                <Typography variant="textLarge" color="text.primary">
                  {content.subtitle}
                </Typography>
              ) : null}
              {content.text ? (
                <Typography
                  variant="textRegular"
                  color="text.secondary"
                  component="div"
                  sx={(theme) => ({
                    '& span': {
                      bgColor: 'red'
                    },
                    '& a': { color: 'primary.main' },
                    '& h2': {
                      fontFamily: theme.typography.h2.fontFamily,
                      fontSize: theme.typography.h2.fontSize,
                      fontWeight: theme.typography.h2.fontWeight,
                      lineHeight: theme.typography.h2.lineHeight,
                      letterSpacing: theme.typography.h2.letterSpacing,
                      marginTop: theme.spacing(4),
                      marginBottom: theme.spacing(2),
                      color: '#212121'
                    },
                    '& h3': {
                      fontFamily: theme.typography.h3.fontFamily,
                      fontSize: theme.typography.h3.fontSize,
                      fontWeight: theme.typography.h3.fontWeight,
                      lineHeight: theme.typography.h3.lineHeight,
                      letterSpacing: theme.typography.h3.letterSpacing,
                      marginTop: theme.spacing(3),
                      marginBottom: theme.spacing(1),
                      color: '#212121'
                    },
                    '& h4': {
                      fontFamily: theme.typography.h4.fontFamily,
                      fontSize: theme.typography.h4.fontSize,
                      fontWeight: theme.typography.h4.fontWeight,
                      lineHeight: theme.typography.h4.lineHeight,
                      letterSpacing: theme.typography.h4.letterSpacing,
                      marginTop: theme.spacing(2),
                      marginBottom: theme.spacing(1),
                      color: '#212121'
                    },
                    '& h5': {
                      fontFamily: theme.typography.h5.fontFamily,
                      fontSize: theme.typography.h5.fontSize,
                      fontWeight: theme.typography.h5.fontWeight,
                      lineHeight: theme.typography.h5.lineHeight,
                      letterSpacing: theme.typography.h5.letterSpacing,
                      marginTop: theme.spacing(2),
                      marginBottom: theme.spacing(1),
                      color: '#212121'
                    },
                    '& h6': {
                      fontFamily: theme.typography.h6.fontFamily,
                      fontSize: theme.typography.h6.fontSize,
                      fontWeight: theme.typography.h6.fontWeight,
                      lineHeight: theme.typography.h6.lineHeight,
                      letterSpacing: theme.typography.h6.letterSpacing,
                      marginTop: theme.spacing(2),
                      marginBottom: theme.spacing(1),
                      color: '#212121'
                    }
                  })}
                >
                  {parse(content.text)}
                </Typography>
              ) : null}
              {button?.link?.url ? (
                <Box sx={{ display: 'flex' }}>
                  <Button
                    href={button.link.url}
                    variant="contained"
                    color="primary"
                    icon={button.icon}
                  >
                    {button.link.title}
                  </Button>
                </Box>
              ) : null}
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  )
}

export default Paragraph
